import { FC, lazy, useRef, useState } from 'react';
import { graphql, useFragment } from 'react-relay';

// components
import { VisibilityTracker } from 'dibs-visibility-tracker/exports/VisibilityTracker';
import { ClientSuspense } from 'dibs-elements/exports/ClientSuspense';
import { SharedRespAuctionMosaicLoading } from '../../shared/SharedRespAuctionMosaicCarousel/SharedRespAuctionMosaicLoading';

// types
import { PdpRespSimilarAuctionItemsLazy_user$key } from './__generated__/PdpRespSimilarAuctionItemsLazy_user.graphql';
import { PdpRespSimilarAuctionItemsLazy_viewer$key } from './__generated__/PdpRespSimilarAuctionItemsLazy_viewer.graphql';
import { PdpRespSimilarAuctionItemsLazy_item$key } from './__generated__/PdpRespSimilarAuctionItemsLazy_item.graphql';

export const PdpRespSimilarAuctionItemsLazyComponent = lazy(
    () =>
        import(/* webpackChunkName: "PdpRespSimilarAuctionItems" */ './PdpRespSimilarAuctionItems')
);

const viewerFragment = graphql`
    fragment PdpRespSimilarAuctionItemsLazy_viewer on Viewer {
        similarAuctionItemsPdpFF: featureFlag(feature: "similarAuctionItemsPDP")
    }
`;

const itemFragment = graphql`
    fragment PdpRespSimilarAuctionItemsLazy_item on Item {
        ...PdpRespSimilarAuctionItems_item
        isAuctionEnabled
    }
`;

const userFragment = graphql`
    fragment PdpRespSimilarAuctionItemsLazy_user on User {
        ...PdpRespSimilarAuctionItems_user
    }
`;

export const PdpRespSimilarAuctionItemsLazy: FC<{
    user: PdpRespSimilarAuctionItemsLazy_user$key;
    viewer: PdpRespSimilarAuctionItemsLazy_viewer$key;
    item: PdpRespSimilarAuctionItemsLazy_item$key;
}> = ({ user: userRef, viewer: viewerRef, item: itemRef }) => {
    const user = useFragment(userFragment, userRef);
    const viewer = useFragment(viewerFragment, viewerRef);
    const item = useFragment(itemFragment, itemRef);

    const isAuctionEnabled = item.isAuctionEnabled;
    const similarAuctionItemsPdpFF = !!viewer.similarAuctionItemsPdpFF;
    const showSimilarAuctionItemsModule = similarAuctionItemsPdpFF && !!isAuctionEnabled;

    const [isVisible, setIsVisible] = useState(false);
    const sectionVisibilityRef = useRef(null);

    if (!showSimilarAuctionItemsModule) {
        return null;
    }

    return (
        <div ref={sectionVisibilityRef}>
            <VisibilityTracker
                elementRef={sectionVisibilityRef}
                onVisibilityChange={({ isVisible: isSectionVisible }) => {
                    setIsVisible(isSectionVisible);
                }}
                observerOptions={{
                    rootMargin: '15% 0px',
                }}
            />
            {isVisible ? (
                <ClientSuspense fallback={<SharedRespAuctionMosaicLoading />}>
                    <PdpRespSimilarAuctionItemsLazyComponent user={user} item={item} />
                </ClientSuspense>
            ) : (
                <SharedRespAuctionMosaicLoading />
            )}
        </div>
    );
};
